import React, { FC, useEffect, useMemo, useState } from 'react'
import { Layout, Nav, Spin } from '@douyinfe/semi-ui'
import { useLocation, useNavigate } from 'react-router'
import { useLocale } from '@src/locales'
import useStore from '@src/store/common/global'
import '../../index.scss'
import { MenuItem } from '@src/api/common/menu'
import useMenuStore from '@src/store/common/menu'
import { NavItems } from '@douyinfe/semi-ui/lib/es/navigation'
import logo_black from '@src/assets/logo_b.svg'
import logo_light from '@src/assets/logo_w.svg'

const { Sider } = Layout

/**
 * 收起后的固定宽度（px）。
 * - 必须和 Nav 的 isCollapsed 视觉宽度对齐：图标居中 + 极少内边距
 * - 不能被全局设置的 siderWidth 覆盖，否则收起时 Sider 容器还是宽的，菜单图标就
 *   被挤到一边而文本却消失，看起来只是文字隐藏了，没有"收起"的感觉
 * - 64 是 Semi 官方收起 Nav 的常用尺寸，够一个 24px 图标 + 左右内边距
 */
const SIDER_COLLAPSED_WIDTH = 64

function renderIcon(icon: any) {
	if (!icon) {
		return null
	}
	return icon.render()
}

function findMenuByPath(menus: MenuItem[], path: string, keys: any[]): any {
	for (const menu of menus) {
		if (menu.path === path) {
			return [...keys, menu.itemKey]
		}
		if (menu.items && menu.items.length > 0) {
			const result = findMenuByPath(menu.items, path, [...keys, menu.itemKey])
			if (result.length === 0) {
				continue
			}
			return result
		}
	}
	return []
}

const Index: FC = () => {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const { formatMessage: i18nMsg } = useLocale()
	const [openKeys, setOpenKeys] = useState<string[]>([])
	const [selectedKeys, setSelectedKeys] = useState<string[]>([])
	const { menuList, fetchMenuList, loading } = useMenuStore()
	const locale = useStore((state) => state.locale)
	const theme = useStore((state) => state.theme)
	const systemName = useStore((state) => state.systemName)
	const siderWidth = useStore((state) => state.siderWidth)
	const [isCollapsed, setIsCollapsed] = useState(false)

	// 收起时用固定窄宽度（不走 siderWidth），展开时再用全局设置值
	const currentSiderWidth = isCollapsed ? SIDER_COLLAPSED_WIDTH : siderWidth

	// 首次加载菜单
	useEffect(() => {
		fetchMenuList()
	}, [fetchMenuList])

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const navList = useMemo<NavItems>(() => {
		if (loading || !menuList.length) return []
		return menuList
			.filter((e) => !e.hide)
			.map((e) => ({
				...e,
				text: i18nMsg({ id: e.text }),
				icon: e?.icon ? renderIcon(e.icon) : null,
				items: e?.items
					? e.items
							.filter((m) => !m.hide)
							.map((m) => ({
								...m,
								text: i18nMsg({ id: m.text }),
								icon: m.icon ? renderIcon(m.icon) : null
							}))
					: []
			}))
	}, [menuList, locale, loading])

	const onSelect = (data) => {
		setSelectedKeys([...data.selectedKeys])
		navigate(data.selectedItems[0].path as string)
	}
	const onOpenChange = (data) => {
		setOpenKeys([...data.openKeys])
	}
	const onCollapseChange = (isCollapsed) => {
		setIsCollapsed(isCollapsed)
	}
	// setSelectedKeys 和 path 双向绑定
	useEffect(() => {
		const keys: string[] = findMenuByPath(menuList, pathname, [])
		setSelectedKeys([keys.pop() as string])
		setOpenKeys(Array.from(new Set([...openKeys, ...keys])))
	}, [pathname])

	return (
		<Sider
			style={{
				backgroundColor: 'var(--semi-color-bg-1)',
				width: currentSiderWidth,
				minWidth: currentSiderWidth,
				maxWidth: currentSiderWidth,
				flex: `0 0 ${currentSiderWidth}px`,
				transition:
					'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), flex-basis 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
			}}
		>
			<Spin spinning={loading}>
				<Nav
					isCollapsed={isCollapsed}
					items={navList}
					openKeys={openKeys}
					selectedKeys={selectedKeys}
					onSelect={onSelect}
					onOpenChange={onOpenChange}
					onCollapseChange={onCollapseChange}
					bodyStyle={{ maxHeight: 'calc(100vh - 180.5px)', height: 'calc(100vh - 180.5px)', overflowY: 'auto' }}
					style={{ width: '100%' }}
					header={{
						logo: (
							<img
								src={theme === 'dark' ? logo_light : logo_black}
								alt="Logo"
								style={{
									display: 'block',
									cursor: 'pointer',
									width: '65px',
									height: '32px'
								}}
							/>
						),
						text: systemName.trim() || 'Plus-One UI',
						link: '/dashboard/workbench'
					}}
					footer={{
						collapseButton: true
					}}
				/>
			</Spin>
		</Sider>
	)
}

export default Index
